import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  LANGUAGE_LABELS,
  VERDICT_LABELS,
  isTerminalVerdict,
  type SubmissionCaseResult,
  type Verdict,
} from '@flarecode/shared';
import { useSubmissionStream } from '../hooks/useSubmissionStream';

export function SubmissionDetailPage() {
  const { id } = useParams();
  const { submission, isLoading, error, totalCases, isCompiling, streamConnected } =
    useSubmissionStream(id);
  const [showSource, setShowSource] = useState(false);

  if (isLoading) return <p>Loading submission…</p>;
  if (error) return <p className="error">Submission not found.</p>;
  if (!submission) return null;

  const running = !isTerminalVerdict(submission.verdict);
  const progressDenom = totalCases ?? submission.cases.length;

  return (
    <section className="submission-detail">
      <div className="submission-header">
        <div>
          <h2>
            <Link to={`/problems/${submission.problemSlug}`}>{submission.problemTitle}</Link>
          </h2>
          <div className="submission-meta">
            <span>{LANGUAGE_LABELS[submission.language]}</span>
            <span>·</span>
            <time dateTime={submission.createdAt}>
              {new Date(submission.createdAt).toLocaleString()}
            </time>
          </div>
        </div>
        <VerdictBadge verdict={submission.verdict} />
      </div>

      {running && (
        <p className="running-indicator">
          {isCompiling
            ? 'Compiling…'
            : streamConnected
              ? 'Judge is working on it… (live stream connected)'
              : 'Judge is working on it… (waiting for progress)'}
        </p>
      )}

      {!running && (
        <dl className="stats">
          <div>
            <dt>Runtime</dt>
            <dd>{submission.runtimeMs != null ? `${submission.runtimeMs} ms` : '—'}</dd>
          </div>
          <div>
            <dt>Memory</dt>
            <dd>
              {submission.memoryKb != null ? `${Math.round(submission.memoryKb / 1024)} MB` : '—'}
            </dd>
          </div>
          {submission.failedCaseIndex != null && (
            <div>
              <dt>Failed case</dt>
              <dd>#{submission.failedCaseIndex + 1}</dd>
            </div>
          )}
        </dl>
      )}

      {submission.compileStderr && (
        <div className="stderr-block">
          <h3>Compile output</h3>
          <pre>{submission.compileStderr}</pre>
        </div>
      )}

      {(submission.cases.length > 0 || progressDenom > 0) && (
        <div className="cases">
          <h3>
            Cases ({submission.cases.filter((c) => c.passed).length}/
            {Math.max(progressDenom, submission.cases.length)} passed so far)
          </h3>
          {submission.cases.map((c) => (
            <CaseRow key={c.index} c={c} />
          ))}
        </div>
      )}

      <div className="source-disclosure">
        <button type="button" className="linklike" onClick={() => setShowSource((s) => !s)}>
          {showSource ? 'Hide source' : 'View source'}
        </button>
        {showSource && (
          <pre className="source-block">
            <code>{submission.source}</code>
          </pre>
        )}
      </div>
    </section>
  );
}

function VerdictBadge({ verdict }: { verdict: Verdict }) {
  return <span className={`verdict-badge verdict--${verdict}`}>{VERDICT_LABELS[verdict]}</span>;
}

function CaseRow({ c }: { c: SubmissionCaseResult }) {
  const [open, setOpen] = useState(!c.passed);
  const isSampleFail = !c.passed && c.input !== undefined;
  const hasDetail = isSampleFail || !!c.stderrSnippet;
  return (
    <div className={`case-row case-row--${c.passed ? 'ok' : 'fail'}`}>
      <div className="case-row-head">
        <span className="case-label">Case #{c.index + 1}</span>
        <span className={`verdict-badge verdict--${c.verdict}`}>{VERDICT_LABELS[c.verdict]}</span>
        <span className="case-stats">
          {c.runtimeMs != null ? `${c.runtimeMs} ms` : '—'}
          {' · '}
          {c.memoryKb != null ? `${Math.round(c.memoryKb / 1024)} MB` : '—'}
        </span>
        {hasDetail && (
          <button type="button" className="linklike" onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide' : 'Details'}
          </button>
        )}
      </div>
      {open && isSampleFail && (
        <div className="case-detail">
          <div className="sample-col">
            <label>Input</label>
            <pre>{(c.input ?? '').trimEnd()}</pre>
          </div>
          <div className="sample-col">
            <label>Expected</label>
            <pre>{(c.expected ?? '').trimEnd()}</pre>
          </div>
          <div className="sample-col">
            <label>Your output</label>
            <pre>{(c.actual ?? '').trimEnd() || <em>(empty)</em>}</pre>
          </div>
        </div>
      )}
      {open && !isSampleFail && !c.passed && (
        <p className="case-hidden-note">
          Hidden test case — input and expected output are not revealed.
        </p>
      )}
      {open && c.stderrSnippet && (
        <div className="stderr-block">
          <label>stderr</label>
          <pre>{c.stderrSnippet}</pre>
        </div>
      )}
    </div>
  );
}
